import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CateringCartService } from '../../../../core/services/catering-cart.service';
import { LocaleService } from '../../../../core/services/locale.service';
import { CateringVendorService } from '../../../../core/services/catering-vendor.service';
import { BookMeetingService } from '../../../../core/services/book-meeting.service';
import { CateringCapabilitiesService } from '../../../../core/services/catering-capabilities.service';
import { CateringApiService } from '../../../../core/services/catering-api.service';
import { LocationStateService } from '../../../../core/services/location-state.service';
import { DietaryPreferencesService } from '../../../../core/services/dietary-preferences.service';
import { BookMeetingView } from '../../../../core/constants/enums';
import { formatCurrencyMinor } from '../../../../core/utils/catering';
import { PaperButtonComponent } from '@ricoh-paper/angular';
import { DietaryPreferencesSetupDialogComponent } from '../dietary-preferences-setup-dialog/dietary-preferences-setup-dialog.component';
import { AllergenBadgesComponent, MenuItemAllergen } from '../allergen-badges/allergen-badges.component';
import { AllergenConflictWarningComponent } from '../allergen-conflict-warning/allergen-conflict-warning.component';

/**
 * CartSummaryComponent — cart review panel content.
 *
 * Shows the ordered items, delivery time, special instructions, dietary
 * confirmation checkbox, cost breakdown (subtotal + total), an approval
 * warning when the total exceeds the location threshold, and a footer
 * with Back / Add to Booking actions.
 *
 * On "Add to Booking" the cart is exported as a CateringOrderDraft via
 * cartService.toDraft() and attached to the meeting via
 * meetingService.addCateringOrder(), then navigates back to the form view.
 */
@Component({
  selector: 'ct-cart-summary',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    DietaryPreferencesSetupDialogComponent,
    AllergenBadgesComponent,
    AllergenConflictWarningComponent,
    PaperButtonComponent,
    TranslocoPipe,
  ],
  template: `
    <!-- Dietary preferences setup dialog -->
    @if (showSetupDialog()) {
      <ct-dietary-preferences-setup
        (preferencesSaved)="onPreferencesSaved($event)"
        (cancelled)="onSetupCancelled()" />
    }

    <div class="cs">
      <!-- ── Item list ────────────────────────────── -->
      <div class="cs__section">
        <div class="cs__section-header">
          <h4 class="cs__section-title">{{ 'catering.customer.cart_summary.your_order' | transloco }}</h4>
          <paper-button class="cs__edit-action" intent="ghost" size="xs" (click)="onEditItems()">{{ 'catering.customer.cart_summary.edit_items' | transloco }}</paper-button>
        </div>

        <table class="cs__items">
          <thead class="sr-only">
            <tr>
              <th scope="col">{{ 'catering.customer.cart_summary.col_quantity' | transloco }}</th>
              <th scope="col">{{ 'catering.customer.cart_summary.col_item' | transloco }}</th>
              <th scope="col">{{ 'catering.customer.cart_summary.col_price' | transloco }}</th>
            </tr>
          </thead>
          <tbody>
            @for (item of cartService.items(); track item.cartLineId) {
              <tr class="cs__item-row">
                <td class="cs__item-qty">{{ item.quantity }}&times;</td>
                <td class="cs__item-name">{{ item.name }}</td>
                <td class="cs__item-price">{{ fmtPence(item.totalCostPence) }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- ── Delivery time ────────────────────────── -->
      <div class="cs__section">
        <label class="cs__label" for="cs-delivery-time">{{ 'catering.customer.cart_summary.delivery_time' | transloco }}</label>
        <input
          id="cs-delivery-time"
          type="time"
          class="cs__time-input"
          [ngModel]="deliveryTimeStr()"
          (ngModelChange)="onDeliveryTimeChanged($event)" />
        @if (vendorClosingTime(); as close) {
          <span class="cs__hint">{{ vendorClosesLabel(close) }}</span>
        }
      </div>

      <!-- ── Notes ────────────────────────────────── -->
      <div class="cs__section">
        <label class="cs__label" for="cs-notes">{{ 'catering.customer.cart_summary.special_instructions' | transloco }}</label>
        <textarea
          id="cs-notes"
          class="cs__textarea ph-mask"
          rows="4"
          [placeholder]="'catering.customer.cart_summary.notes_placeholder' | transloco"
          [ngModel]="cartService.notes()"
          (ngModelChange)="cartService.setNotes($event)"></textarea>
      </div>

      <!-- ── Allergen conflict warning ───────────── -->
      @if (showAllergenWarning()) {
        <ct-allergen-conflict-warning
          [conflicts]="allergenConflicts()"
          (editPreferences)="onEditPreferences()" />
      }

      <!-- ── Dietary confirmation ─────────────────── -->
      @if (hasDietaryItems()) {
        <div class="cs__dietary-section">
          <label class="cs__checkbox-row">
            <input
              type="checkbox"
              class="cs__checkbox"
              data-testid="dietary-confirm-checkbox"
              [ngModel]="cartService.dietaryConfirmed()"
              (ngModelChange)="cartService.setDietaryConfirmed($event)" />
            <span class="cs__checkbox-label">
              {{ 'catering.customer.cart_summary.dietary_confirm' | transloco }}
            </span>
          </label>

          <!-- Show cart allergens -->
          @if (cartAllergens().length > 0) {
            <div class="cs__cart-allergens">
              <span class="cs__allergen-label">{{ 'catering.customer.cart_summary.contains' | transloco }}</span>
              <ct-allergen-badges [allergens]="cartAllergens()" [maxVisible]="5" />
            </div>
          }

          <paper-button intent="ghost" size="xs" class="cs__edit-action cs__edit-prefs-link" (click)="onEditPreferences()">
            {{ 'catering.customer.cart_summary.edit_dietary_prefs' | transloco }}
          </paper-button>
        </div>
      }

      <!-- ── Cost breakdown ───────────────────────── -->
      <div class="cs__costs">
        @if (cartService.taxPence() > 0) {
          <div class="cs__cost-row">
            <span class="cs__cost-label">{{ 'catering.customer.cart_summary.subtotal' | transloco }}</span>
            <span class="cs__cost-value">{{ formattedSubtotal() }}</span>
          </div>
          <div class="cs__cost-row">
            <span class="cs__cost-label">{{ 'catering.customer.cart_summary.tax' | transloco }}</span>
            <span class="cs__cost-value">{{ formattedTax() }}</span>
          </div>
        }
        <div class="cs__cost-row cs__cost-row--total">
          <span class="cs__cost-label">{{ 'catering.customer.cart_summary.total' | transloco }}</span>
          <span class="cs__cost-value cs__cost-value--total"><span class="sr-only">{{ 'catering.customer.cart_summary.order_total_sr' | transloco }}</span>{{ formattedTotal() }}</span>
        </div>
      </div>

      <!-- ── Cart issues (server-side validation) ─── -->
      @if (cartService.cartIssues().length) {
        <div class="cs__issues" role="alert">
          <strong class="cs__issues-title">{{ 'catering.customer.cart_summary.review_cart' | transloco }}</strong>
          <ul class="cs__issues-list">
            @for (issue of cartService.cartIssues(); track issue.menuItemId + issue.issueType) {
              <li>{{ issue.itemName }}: {{ issue.message }}</li>
            }
          </ul>
        </div>
      }

      <!-- ── Approval warning ─────────────────────── -->
      @if (showApprovalWarning()) {
        <div class="cs__approval-warning">
          {{ approvalLabel() }}
        </div>
      }

      <!-- ── Footer ───────────────────────────────── -->
      <div class="cs__footer">
        <paper-button intent="ghost" size="sm" icon="arrow-left" (click)="onBack()">
          {{ 'catering.customer.cart_summary.back' | transloco }}
        </paper-button>
        <paper-button
          intent="primary"
          size="sm"
          class="cs__submit"
          data-testid="add-to-booking-btn"
          [disabled]="!cartService.canSubmit()"
          (click)="onAddToBooking()">
          {{ 'catering.customer.cart_summary.add_to_booking' | transloco }}
        </paper-button>
      </div>
    </div>
  `,
  styles: [`
    .cs {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    /* ── Sections ──────────────────────────── */

    .cs__section {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .cs__section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .cs__section-title {
      margin: 0;
      font-size: 12px;
      font-weight: 600;
      color: var(--text-secondary);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .cs__label {
      font-size: 11px;
      font-weight: 600;
      color: var(--text-secondary);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .cs__edit-action {
      --text-secondary: var(--color-primary-text);
      --text-primary: var(--color-primary-text);
    }

    /* ── Items table ──────────────────────── */

    .cs__items {
      width: 100%;
      border-spacing: 0;
      font-size: 13px; /* design flow-cart: order lines read at 13px */
      font-variant-numeric: tabular-nums;
    }

    .cs__item-row td {
      padding: 8px 0;
      vertical-align: top;
      /* design flow-cart: hairline between order lines */
      border-bottom: 1px solid var(--border-low);
    }

    .cs__item-row:last-child td {
      border-bottom: none;
    }

    .cs__item-qty {
      width: 28px;
      color: var(--text-tertiary);
      font-weight: 500;
    }

    .cs__item-name {
      color: var(--text);
    }

    .cs__item-price {
      width: 60px;
      text-align: right;
      color: var(--text);
      font-weight: 500;
    }

    /* ── Delivery time ────────────────────── */

    .cs__time-input {
      width: 100%;
      padding: 8px 12px;
      font-size: 13px;
      color: var(--text);
      background: var(--surface-2);
      border: 1px solid var(--border-low);
      border-radius: var(--radius-sm);
      outline: none;
      transition: border-color 0.15s ease;

      &:focus { border-color: var(--color-primary); }
    }

    .cs__hint {
      font-size: 11px;
      color: var(--text-tertiary);
    }

    /* ── Textarea ─────────────────────────── */

    .cs__textarea {
      width: 100%;
      padding: 8px 12px;
      font-size: 13px;
      font-family: inherit;
      color: var(--text);
      background: var(--surface-2);
      border: 1px solid var(--border-low);
      border-radius: var(--radius-sm);
      outline: none;
      resize: vertical;
      transition: border-color 0.15s ease;

      &::placeholder { color: var(--text-tertiary); }
      &:focus { border-color: var(--color-primary); }
    }

    /* ── Checkbox ─────────────────────────── */

    .cs__dietary-section {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .cs__checkbox-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      cursor: pointer;
    }

    .cs__checkbox {
      width: 16px;
      height: 16px;
      margin-top: 1px;
      accent-color: var(--color-primary);
      cursor: pointer;
    }

    .cs__checkbox-label {
      font-size: 12px;
      color: var(--text-secondary);
      line-height: 1.4;
    }

    .cs__cart-allergens {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      background: var(--surface-2);
      border: 1px solid var(--border-low);
      border-radius: var(--radius-sm);
    }

    .cs__allergen-label {
      font-size: 11px;
      font-weight: 600;
      color: var(--text-tertiary);
      flex-shrink: 0;
    }

    .cs__edit-prefs-link {
      align-self: flex-start;
    }

    /* ── Cost breakdown ───────────────────── */

    .cs__costs {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding-top: 8px;
      border-top: 1px solid var(--border-low);
    }

    .cs__cost-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 12px;
      font-variant-numeric: tabular-nums;
    }

    .cs__cost-label {
      color: var(--text-secondary);
    }

    .cs__cost-value {
      color: var(--text);
      font-weight: 500;
    }

    .cs__cost-row--total {
      padding-top: 6px;
    }

    .cs__cost-value--total {
      /* design flow-cart: the order total is the loudest number on the
         review screen (large + bold, tabular). */
      font-size: 20px;
      font-weight: 700;
      color: var(--text);
    }

    /* ── Cart issues (blocking) ───────────── */

    .cs__issues {
      padding: 10px 12px;
      font-size: 11px;
      line-height: 1.5;
      color: var(--error-text);
      background: color-mix(in srgb, var(--danger) 10%, transparent);
      border: 1px solid color-mix(in srgb, var(--danger) 30%, transparent);
      border-radius: var(--radius-sm);
    }

    .cs__issues-title {
      display: block;
      margin-bottom: 4px;
    }

    .cs__issues-list {
      margin: 0;
      padding-left: 18px;
    }

    /* ── Approval warning ─────────────────── */

    .cs__approval-warning {
      padding: 10px 12px;
      font-size: 11px;
      line-height: 1.5;
      color: var(--highlight-warning-text);
      background: var(--highlight-warning-bg);
      border: 1px solid var(--highlight-warning-border);
      border-radius: var(--radius-sm);
    }

    /* ── Footer ───────────────────────────── */

    .cs__footer {
      display: flex;
      align-items: center;
      gap: 10px;
      padding-top: 12px;
      border-top: 1px solid var(--border-low);
    }

    .cs__submit {
      margin-left: auto;
    }

    @media (prefers-reduced-motion: reduce) {
      .cs__time-input,
      .cs__textarea {
        transition: none;
      }
    }
  `],
})
export class CartSummaryComponent {
  protected readonly cartService = inject(CateringCartService);
  private readonly meetingService = inject(BookMeetingService);
  private readonly vendorService = inject(CateringVendorService);
  private readonly capabilitiesService = inject(CateringCapabilitiesService);
  private readonly cateringApi = inject(CateringApiService);
  private readonly locationState = inject(LocationStateService);
  private readonly dietaryPrefsService = inject(DietaryPreferencesService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  protected vendorClosesLabel(close: string): string {
    this.locale.locale();
    return this.transloco.translate('catering.customer.cart_summary.vendor_closes', { time: close });
  }

  protected approvalLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('catering.customer.cart_summary.approval_warning', { threshold: this.formattedThreshold() });
  });

  // Server-side approval requirement, refreshed when cart total changes.
  // Falls back to local threshold check while in flight.
  private readonly _serverApprovalRequired = signal<boolean | null>(null);
  private _approvalCheckTimer: ReturnType<typeof setTimeout> | null = null;

  // Dietary preferences setup dialog state
  protected readonly showSetupDialog = signal(false);

  constructor() {
    effect(() => {
      const locationId = this.locationState.selectedLocationId();
      // Inc-tax: the server judges the gate on the order's total, and an
      // ex-tax figure called every basket just under the gate clear.
      const totalPence = this.cartService.grossPence();
      if (!locationId || totalPence <= 0) {
        this._serverApprovalRequired.set(null);
        return;
      }

      // Debounce: cart total churns as user adds/removes items.
      if (this._approvalCheckTimer) clearTimeout(this._approvalCheckTimer);
      this._approvalCheckTimer = setTimeout(async () => {
        try {
          const result = await this.cateringApi.checkApproval(locationId, totalPence);
          this._serverApprovalRequired.set(result.required);
        } catch (err) {
          console.warn('[CartSummary] approval check failed:', err);
          this._serverApprovalRequired.set(null);
        }
      }, 300);
    });
  }

  // ── Computed ──────────────────────────────────────

  protected readonly formattedSubtotal = computed(() =>
    formatCurrencyMinor(this.cartService.totalPence(), this.cartService.currency()),
  );

  protected readonly formattedTax = computed(() =>
    formatCurrencyMinor(this.cartService.taxPence(), this.cartService.currency()),
  );

  protected readonly formattedTotal = computed(() =>
    formatCurrencyMinor(this.cartService.grossPence(), this.cartService.currency()),
  );

  protected readonly deliveryTimeStr = computed(() => {
    const dt = this.cartService.deliveryTime();
    if (!dt) return '';
    const h = String(dt.getHours()).padStart(2, '0');
    const m = String(dt.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  });

  protected readonly vendorClosingTime = computed(() => {
    const vendor = this.vendorService.selectedVendor();
    if (!vendor?.operatingHours?.length) return null;
    const day = new Date().getDay();
    const hours = vendor.operatingHours.find(h => h.dayOfWeek === day);
    return hours?.close ?? null;
  });

  protected readonly hasDietaryItems = computed(() =>
    this.cartService.items().some(item => (item.dietaryTags?.length ?? 0) > 0),
  );

  protected readonly showApprovalWarning = computed(() => {
    const serverResult = this._serverApprovalRequired();
    if (serverResult !== null) return serverResult;

    // Fallback to cached threshold while server check is pending or failed.
    const locationId = this.locationState.selectedLocationId();
    if (!locationId) return false;
    const caps = this.capabilitiesService.capabilities().get(locationId);
    if (!caps?.approvalEnabled || caps.approvalThresholdPence == null) return false;
    // The server's own comparison: inc-tax total, and AT the gate needs approval.
    return this.cartService.grossPence() >= caps.approvalThresholdPence;
  });

  protected readonly formattedThreshold = computed(() => {
    const locationId = this.locationState.selectedLocationId();
    if (!locationId) return '';
    const caps = this.capabilitiesService.capabilities().get(locationId);
    if (caps?.approvalThresholdPence == null) return '';
    return formatCurrencyMinor(caps.approvalThresholdPence, this.cartService.currency());
  });

  /** Allergen IDs that are in cart AND in user's exclusion list */
  protected readonly allergenConflicts = computed(() => {
    const prefs = this.dietaryPrefsService.preferences();
    if (!prefs.allergenExclusions.length) return [];
    return this.cartService.getAllergenConflicts(prefs.allergenExclusions);
  });

  /** Show allergen warning banner if conflicts exist */
  protected readonly showAllergenWarning = computed(() =>
    this.allergenConflicts().length > 0,
  );

  /** All unique allergens present in cart (for display near dietary confirmation) */
  protected readonly cartAllergens = computed((): MenuItemAllergen[] => {
    const allergenMap = new Map<string, MenuItemAllergen>();
    this.cartService.items().forEach(item => {
      item.allergens?.forEach(a => {
        if (!allergenMap.has(a.id)) {
          allergenMap.set(a.id, a);
        }
      });
    });
    return Array.from(allergenMap.values());
  });

  // ── Helpers ───────────────────────────────────────

  protected fmtPence(pence: number): string {
    return formatCurrencyMinor(pence, this.cartService.currency());
  }

  // ── Actions ───────────────────────────────────────

  protected onDeliveryTimeChanged(timeStr: string): void {
    if (!timeStr) return;
    const [h, m] = timeStr.split(':').map(Number);
    const dt = new Date();
    dt.setHours(h, m, 0, 0);
    this.cartService.setDeliveryTime(dt);
  }

  protected onEditItems(): void {
    this.meetingService.navigateTo(BookMeetingView.MENU_BROWSE);
  }

  protected onBack(): void {
    this.meetingService.navigateTo(BookMeetingView.MENU_BROWSE);
  }

  protected onAddToBooking(): void {
    const room = this.meetingService.activeRoomForCatering();
    if (!room) return;

    const dayIndex = this.meetingService.isMultiDay() ? this.meetingService.activeDayIndex() : undefined;
    const draft = this.cartService.toDraft(room.spaceId, room.name, dayIndex);
    this.meetingService.addCateringOrder(draft);
    this.cartService.reset();
    this.meetingService.backToForm();
  }

  protected onEditPreferences(): void {
    this.showSetupDialog.set(true);
  }

  protected onPreferencesSaved(event: { dietaryTags: string[]; allergens: string[] }): void {
    // Update preferences
    this.dietaryPrefsService.update({
      dietaryTags: event.dietaryTags,
      allergenExclusions: event.allergens,
    });
    this.showSetupDialog.set(false);
  }

  protected onSetupCancelled(): void {
    this.showSetupDialog.set(false);
  }
}
